/**
 * HTTP client.
 *
 * The token is held in localStorage so a page reload keeps the session. That is
 * a deliberate trade-off for an internal tool: it is readable by any script on
 * the origin, which is acceptable here and should be revisited if the platform
 * ever renders third-party content.
 */

// Imported directly rather than via useI18n(): this throws outside React.
import { t } from "@/i18n/t";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
const TOKEN_KEY = "ai2sales.token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly path: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** Notified on a 401 so the app can drop to the login screen. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/** Pull a readable message out of a FastAPI error body. */
async function errorMessage(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) return response.statusText || `HTTP ${response.status}`;

  try {
    const body = JSON.parse(text) as { detail?: unknown };
    const detail = body.detail;

    if (typeof detail === "string") return detail;

    // 422 from Pydantic: detail is a list of {loc, msg}.
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (item as { msg?: string }).msg)
        .filter((msg): msg is string => Boolean(msg));
      if (messages.length) return messages.join("; ");
    }
  } catch {
    /* not JSON - fall through to the raw text */
  }
  return text;
}

async function request<T>(method: string, path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const headers = new Headers(init?.headers);

  // Never set Content-Type for FormData: the browser must add the multipart
  // boundary itself.
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE_URL}${path}`, { ...init, method, headers });

  if (response.status === 401) {
    setToken(null);
    onUnauthorized?.();
    throw new ApiError(401, t("api.sessionExpired"), path);
  }

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response), path);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) =>
    request<T>("POST", path, body === undefined ? undefined : { body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>("PUT", path, body === undefined ? undefined : { body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>("PATCH", path, body === undefined ? undefined : { body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>("POST", path, { body: form });
  },
};
